import { createSlice } from "@reduxjs/toolkit";
import { createAppAsyncThunk } from "@/store/createAppAsyncThunk";
import { apiClient } from "../../api/apiClient";
import { fetchEstatesByIds } from "../estate/estateList/estateListSlice";
import { setCurrentEstateByIdThunk } from "../estate/currentEstate/currentEstateSlice";
import { clearState } from "../../localStorage";
import { openNotification } from "../uiState";
import { hasEntries } from "../../functions/array";
import { accountActions } from "../accounts/accountSlice";
import type { RootState } from "../../store/store";

export interface UserRelation {
  estateId: number;
  livingUnitId?: number;
  [key: string]: any;
}

export interface User {
  id?: number;
  accountId?: number;
  relations?: UserRelation[];
  filteredServiceTypeIds?: number[];
  [key: string]: any;
}

interface SessionData {
  user: {
    accountId: number;
    [key: string]: any;
  };
}

interface AccountData {
  person: {
    id: number;
    [key: string]: any;
  };
}

const initialState: User = {};

// Slice
const userSlice = createSlice({
  name: "currentUser",
  initialState,
  reducers: {
    clearUser: () => {
      return {};
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCurrentUserThunk.fulfilled, (state, action) => {
      if (action.payload) {
        return action.payload;
      }
    });
  },
});

export const { clearUser } = userSlice.actions;

// Async thunks
export const fetchCurrentUserThunk = createAppAsyncThunk<
  User | undefined,
  void,
  {
    rejectValue: { messageId: string; userData?: User };
  }
>(
  "currentUser/fetchCurrentUser",
  async (_, { dispatch, getState, rejectWithValue }) => {
    const token = getState().sessionToken;

    const sessionResponse = await apiClient().get<SessionData>(
      `sessions/${token}`,
    );
    if (sessionResponse.status === 200) {
      const { data: sessionData } = sessionResponse;
      const accountId = sessionData.user.accountId;
      dispatch(accountActions.setData(sessionData.user));

      // get user data
      const accountResponse = await apiClient().get<AccountData>(
        `accounts/${accountId}`,
      );

      if (accountResponse.status === 200) {
        const { person } = accountResponse.data;
        const personResponse = await apiClient().get<User>(
          `persons/${accountResponse.data.person.id}`,
        );

        const { data } = personResponse;

        // ! hack, normally we should get all information from person endpoint, but this is currently not the case
        // ! relations property is missing in person response
        // ! service type ids is missing in account response
        // ! I need to merge booth data to get the needed data
        const userData: User = {
          ...data,
          ...person,
        };

        if (userData != null && !userData.hasOwnProperty("relations")) {
          // You cannot use the app without additional user data.
          console.warn("Missing relation in user object.", userData);
          return rejectWithValue({
            messageId: "ErrorMessage.missingUserRelations",
            userData,
          });
        }

        if (userData != null && hasEntries(userData.relations)) {
          // set all optional estates for the current user
          const estateIds = userData.relations!.map(
            (relation) => relation.estateId,
          );

          dispatch(fetchEstatesByIds(estateIds));

          try {
            const { currentEstate } = getState().estates;
            // if an estate is already set, this should be skipped
            if (Object.keys(currentEstate).length === 0) {
              dispatch(setCurrentEstateByIdThunk(estateIds[0]));
            }
          } catch (err) {
            console.error(
              "Error occurs while getting current estate state.",
              err,
            );
          }

          return userData;
        }
      }
    }
  },
);

export const logoutUser = createAppAsyncThunk<boolean, void, {}>(
  "currentUser/logout",
  async (_, { dispatch }) => {
    console.log("Logging out user...");
    try {
      const response = await apiClient().delete("/sessions/_self");
      if (response.status === 200) {
        dispatch(openNotification("Logged out successfully."));
      } else {
        dispatch(
          openNotification("Error logging out, please contact your admin."),
        );
        console.error(
          "Error logging out, please contact your admin.",
          response,
        );
      }
      return true;
    } catch (err) {
      console.error("Problems while fetching account or session data.", err);
      return false;
    } finally {
      dispatch(clearUser());
      clearState();
    }
  },
);

export const reducer = userSlice.reducer;

// selectors
export const selectCurrentUser = (state: RootState): User => state.currentUser;

// Inferred type from selector for convenience
export type CurrentUser = ReturnType<typeof selectCurrentUser>;
